import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSiteContext} from '../contexts/site'
import {
    getCartLineUnitPrice,
    getColorBarStonesNames,
    getDiscount,
    getItemBadge,
    getLineItemBadge,
    getPriceData
} from '../utils/product-utils'
import {COLORBAR_BADGE, BIRTHDAY_MONTHS} from '../constants'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useGlobaleContext} from '../contexts/globale'
import {getSubTotal, getPurchaseValue} from '../utils/basket-utils'

function getActiveCurrency(overrideCurrency) {
    if (overrideCurrency) {
        return overrideCurrency
    }

    if (typeof window !== 'undefined' && window.__geCurrency) {
        return window.__geCurrency
    }

    return 'USD'
}

const getGtmEnabled = () => {
    try {
        const {
            app: {commerceAPI}
        } = getConfig()
        const {config} = commerceAPI?.tagManager || {}
        return config ? JSON.parse(config).gtmEnabled : false
    } catch (e) {
        console.log(e.message)
        return false
    }
}

// Basket line items persist the originating list attribution (set at add-to-cart)
// as the JSON custom attribute c_analyticsAttributes = {listID, listName, itemSort}.
const parseAnalyticsAttributes = (raw) => {
    if (!raw) return {}
    try {
        return JSON.parse(raw) || {}
    } catch (e) {
        return {}
    }
}

export const normalizeItemName = (name) => (typeof name === 'string' ? name.trim() : name)

const getChangeUpProductID = () => {
    // ChangeUp config
    const {
        app: {commerceAPI}
    } = getConfig()
    const {changeUp} = commerceAPI
    const {configChangeup} = changeUp || {}
    var changeUpProductID = null
    try {
        const jsonConfig = JSON.parse(configChangeup)
        if (jsonConfig) {
            changeUpProductID = jsonConfig.enabled ? jsonConfig.productID : null
        }
    } catch (e) {
        console.error(e.message)
    }
    return changeUpProductID
}

const getPDItemsArray = (lineItems) => {
    let items = []
    lineItems?.forEach((item) => {
        items.push({
            item_id: item.sku,
            item_name: normalizeItemName(item.name),
            price: item.price?.amount,
            quantity: item.quantity
        })
    })

    return items
}

const pushDataLayer = (...args) => {
    if (typeof window === 'undefined') {
        return
    }

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(...args)
}

export const getSelectItemVariantData = (item, variant) => {
    const variants = item?.product?.variants || []
    const itemVariantId = item?.variant?.productId || item?.variant?.id
    const representedProductId = item?.product?.representedProduct?.id
    const findVariant = (productId) =>
        variants.find((candidate) => candidate.productId === productId)
    const selectedVariant =
        findVariant(variant?.pid) ||
        findVariant(itemVariantId) ||
        findVariant(representedProductId) ||
        variants[0]

    return {
        itemId: selectedVariant?.productId || itemVariantId || item?.product?.id,
        selectedVariant
    }
}

export default function useTagManager() {
    const {isYR} = useSiteContext()
    const {data: basket} = useCurrentBasket()
    const {currencyCode: geCurrencyCode} = useGlobaleContext()
    const googleTagManagerEnabled = getGtmEnabled()

    if (typeof window !== 'undefined' && geCurrencyCode) {
        window.__geCurrency = geCurrencyCode
    }

    return {
        push(...args) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push(...args)
        },
        formFieldChange(eventAction, eventLabel) {
            if (!getGtmEnabled()) {
                return false
            }
            this.push({
                event: 'form-field-change',
                event_category: 'Form Field Tracking',
                event_action: eventAction,
                event_label: eventLabel
            })
        },
        runAfterDataLayerLoad(callback) {
            if (typeof window === 'undefined') return

            if (window.__dataLayerLoaded) {
                callback()
                return
            }

            const handler = () => {
                callback()
                window.removeEventListener('datalayer_loaded_event', handler)
            }

            window.addEventListener('datalayer_loaded_event', handler)
        },

        async dataLayerLoad(
            pageType,
            loggedInStatus,
            breadcrumb,
            geCountry,
            geCurrency,
            customer,
            city,
            country,
            state,
            categoryId = null,
            checkoutMode = null,
            pageCategory = 'other'
        ) {
            if (!getGtmEnabled() || typeof window === 'undefined') {
                return false
            }

            const totalItems = basket?.productItems?.reduce((s, i) => s + i?.quantity, 0)
            const totalValue = basket?.productTotal
            pushDataLayer({ecommerce: null})
            window.__geCurrency = geCurrency || window.__geCurrency || 'USD'

            const visitorLoginState =
                customer?.authType === 'registered'
                    ? customer?.c_clubKendraMembershipActive
                        ? 'ck_member'
                        : 'registered'
                    : 'guest'

            const normalizedPageType = (pageType || '').toLowerCase()

            let landingPageType = localStorage.getItem('landing_page_type')

            if (!landingPageType) {
                landingPageType = normalizedPageType
                localStorage.setItem('landing_page_type', landingPageType)
            }

            let data = {
                // new parameters
                event: 'datalayer_loaded',
                page_title: document.title,
                page_location: window.location.href,
                page_type: normalizedPageType,
                page_category: normalizedPageType === 'home' ? normalizedPageType : pageCategory,
                page_path: window.location.pathname,
                landing_page_type: landingPageType.toLowerCase(),
                logged_in_status: loggedInStatus.toString(),
                breadcrumb: breadcrumb || null,
                ge_country: geCountry || 'US',
                ge_currency: geCurrency || 'USD',
                visitor_login_state: visitorLoginState,
                loyalty_state: String(loggedInStatus),
                site_brand: isYR ? 'yellow-rose' : 'kendra-scott',
                city,
                country,
                state,
                cart_total_items: totalItems || 0,
                cart_total_value: totalValue || 0,

                // old Parameters
                pageType: pageType,
                loggedInStatus: loggedInStatus,
                GECountry: geCountry || 'US', // hardcode the default country
                GECurrency: geCurrency || 'USD', // hardcode the default currency
                visitorLoginState:
                    customer?.authType === 'registered'
                        ? customer?.c_clubKendraMembershipActive
                            ? 'ck_member'
                            : 'registered'
                        : 'guest',
                siteBrand: isYR ? 'yellow-rose' : 'kendra-scott'
            }

            if (checkoutMode != null) {
                data.checkout_mode = checkoutMode ? 'bopis' : 'non_bopis'
            }

            if (categoryId) {
                data.category_id = categoryId
            }

            const hashEmail = async (email) => {
                const encoder = new TextEncoder()
                const data = encoder.encode(email)
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
                const hashArray = Array.from(new Uint8Array(hashBuffer))
                const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
                return hashHex
            }

            if (customer?.authType === 'registered' || customer?.authType === 'ck_member') {
                const hashedEmail = customer?.email ? await hashEmail(customer?.email) : ''
                const upper = customer?.c_birthdayMonth?.toUpperCase()
                const foundMonth = BIRTHDAY_MONTHS.find((month) => month.id === upper)

                data.user_id = customer?.customerNo
                data.email1 = customer?.email
                data.email256 = hashedEmail
                data.birth_month = foundMonth?.value
                data.club_kendra_tier = customer?.c_clubKendraMembershipActive ? 'Yes' : undefined
            } else {
                data.user_id = undefined
                data.email1 = undefined
                data.email256 = undefined
                data.birth_month = undefined
                data.club_kendra_tier = undefined
            }

            // Push event
            window.dataLayer.push(data)

            const event = new CustomEvent('datalayer_loaded_event', {detail: data})
            window.dispatchEvent(event)

            this.push({event: 'gtm.js'})
        },
        navigationMain(navLocation, level1Nav, level2Nav, subCategory, navElement = null) {
            if (!getGtmEnabled()) {
                return false
            }

            this.push({
                event: 'navigation',
                nav_location: navLocation,
                level1nav: level1Nav?.toLowerCase(),
                level2nav: level2Nav?.toLowerCase(),
                subcategory: subCategory?.toLowerCase(),
                nav_element: navElement || 'text-link' // hardcode
            })
        },
        navigationCategoryCard(level1Nav, level2Nav, subCategory) {
            if (!getGtmEnabled()) {
                return false
            }
            this.push({
                event: 'navigation',
                nav_location: 'header', // hardcode
                level1nav: level1Nav?.toLowerCase(),
                level2nav: level2Nav?.toLowerCase(),
                subcategory: subCategory?.toLowerCase(),
                nav_element: 'category-card' // hardcode
            })
        },
        productsFilter(filterPayload) {
            if (!getGtmEnabled()) {
                return false
            }
            this.push(filterPayload)
        },
        siteSearch(searchTerm, searchResultsCount, searchCategory, searchType, suggestedTerm) {
            if (!getGtmEnabled()) {
                return
            }
            this.push({
                event: 'site-search',
                search_term: searchTerm,
                search_results: searchResultsCount,
                search_category: searchCategory,
                search_type: searchType,
                suggested_term: suggestedTerm
            })
        },
        productData(
            product,
            selectedVariant,
            index,
            isAddToCart,
            category,
            listId,
            isEngravable,
            isColorbar,
            isGiftCard,
            componentProps = {}
        ) {
            const priceData = product?.priceData
            if (!googleTagManagerEnabled) {
                return false
            }
            var imageBadges = product?.c_imageBadges
            var textBadges = product?.c_textBadges
            if (category?.id) {
                imageBadges = imageBadges?.filter(
                    (b) =>
                        !(
                            category?.id?.toLowerCase()?.includes('sellers') &&
                            b?.toLowerCase()?.includes('best-seller')
                        ) &&
                        !(
                            category?.id?.toLowerCase()?.includes('arrivals') &&
                            b?.toLowerCase()?.includes('new-arrival')
                        )
                )
            }

            var promoBadges
            if (product?.c_promotions?.length) {
                promoBadges = product?.c_promotions?.map((b) => b.name)
            }
            var imageBadgesToShow = imageBadges?.slice(0, 2)
            if (imageBadgesToShow?.length) {
                imageBadgesToShow = imageBadgesToShow.map(
                    (b) => b.match(/data-badge-name="([^"]+)"/)?.[1]
                )
            }

            if (listId === 'colorbar') {
                imageBadgesToShow = [COLORBAR_BADGE]
            }

            if (textBadges?.length) {
                textBadges = textBadges.map((b) => b.match(/data-badge-name="([^"]+)"/)?.[1])
            }

            var result = {
                name: selectedVariant?.name || product?.productName || product?.name,
                quantity: 1, // hardcode
                id: isColorbar
                    ? 'colorbar'
                    : selectedVariant?.productId || product.productId || product.id,
                parent_id: product?.type?.set
                    ? product?.id
                    : product?.c_masterVariantID || product.id,
                price:
                    Math.round(
                        (priceData?.currentPrice ||
                            product?.priceData?.listPrice ||
                            selectedVariant?.tieredPrices?.[0]?.price ||
                            product.price) * 100
                    ) / 100,
                brand: 'Kendra Scott', // Hardcode since API doesn't provide this value
                category:
                    product?.c_primaryCategory ||
                    product?.primaryCategoryId ||
                    product?.c_categoryId ||
                    category,
                position: index,
                item_size:
                    product?.c_size || product?.c_RingSize?.[0] || product?.c_size || undefined,
                dimension92: imageBadgesToShow?.length ? imageBadgesToShow?.join(',') : 'no badge', // for the 'Phase 1', imageBadges are excluded
                dimension108: product?.c_classCode || undefined,
                dimension109: product?.c_subclassCode || undefined,
                'product-badge': imageBadgesToShow?.length
                    ? imageBadgesToShow?.join('|')
                    : undefined,
                'text-badge': textBadges?.length ? textBadges?.join('|') : undefined,
                product_promo: promoBadges?.length ? promoBadges?.join(',') : '',
                dimension110: product?.c_departmentCode || undefined,
                dimension115: false, // there is no add to bag modal event in the Phase 1
                dimension112: product?.c_hideNameAndPrice || undefined,
                dimension113: product?.c_hideNameAndPrice
                    ? selectedVariant?.name || product.name
                    : undefined,

                dimension203: product?.c_program,
                dimension204: product?.c_seasonGeneral,
                dimension205: product?.c_itemOrderType || undefined,
                dimension206: product?.c_giftCard || undefined,
                dimension208: product?.c_colorGeneral || undefined,
                dimension209: product?.c_itemStatus || undefined,
                dimension210: product?.c_customStatus || undefined,
                dimension211: product?.c_highValue || undefined,
                dimension212: product?.c_storeProtectionLevel || undefined,
                dimension215: product?.c_engagement || undefined,
                dimension216: product?.c_noDiff || undefined,
                metric12: product?.c_bvReviewCount ? parseInt(product?.c_bvReviewCount) : 0,
                metric13: product?.c_bvAverageRating ? parseFloat(product?.c_bvAverageRating) : 0,
                dimension88: product?.c_bvReviewCount ? parseInt(product?.c_bvReviewCount) : 0,
                dimension87: product?.c_bvAverageRating
                    ? parseFloat(product?.c_bvAverageRating)
                    : 0,
                product_search_term:
                    document?.querySelector('.sf-product-list-page')?.getAttribute('data-type') ===
                    'srp'
                        ? window.localStorage?.getItem('product_search_term') || undefined
                        : undefined
            }
            result.discount = getDiscount(priceData, 1)
            result.item_badge = getItemBadge(product, {selectedVariant})
            if (listId) {
                result.listId = listId
            }
            if (category?.id || product?.c_primaryCategory) {
                result.category_id = category?.id || product?.c_primaryCategory
            }

            if (isAddToCart) {
                result.quantity = 1 // NOTE: Hardcode the quantity. If in future we implement quantity selector, make this dynamic.
            }

            if (isEngravable) {
                result.engravable = 'true'
            }

            if (isGiftCard) {
                result.list = 'egift card'
            }

            if (
                product?.variationValues?.metal ||
                product?.variationValues?.stoneColor ||
                product?.variationValues?.colorGeneral
            ) {
                result.itemColor = (
                    product?.variationValues?.metal ||
                    product?.variationValues?.stoneColor ||
                    product?.variationValues?.colorGeneral
                )
                    ?.toLowerCase()
                    ?.replace(/\b\w/g, (c) => c.toUpperCase())
            }

            if (componentProps && Object.keys(componentProps).length > 0) {
                const productId = componentProps.id || result.id
                let storedData = {}

                if (!componentProps?.tile_name) componentProps.tile_name = result.name
                if (!componentProps?.image_url)
                    componentProps.image_url = product?.c_images?.mediumImage
                if (!componentProps?.link_url) {
                    const productUrl = product?.productType?.set
                        ? product?.c_url
                        : product?.c_allPdpLinks?.find((p) => p.id === selectedVariant?.productId)
                              ?.url ||
                          product?.slugUrl ||
                          product?.c_url
                    componentProps.link_url = productUrl
                }

                try {
                    storedData = JSON.parse(localStorage.getItem('addedComponentProps')) || {}
                } catch (e) {
                    storedData = {}
                }

                // Initialize array for this productId if missing
                if (!Array.isArray(storedData[productId])) {
                    storedData[productId] = []
                }

                try {
                    const {id, ...analyticsProps} = componentProps
                    result.component_attribution = analyticsProps
                    const pid = productId || id
                    // componentProps
                    storedData[pid].push(componentProps)
                    localStorage.setItem('addedComponentProps', JSON.stringify(storedData))
                } catch (e) {
                    console.warn(e.message)
                }
            }

            return result
        },
        productImpression(hits, breadcrumb, category, listId) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.runAfterDataLayerLoad(() => {
                const impressions = hits?.map((product) => {
                    const priceData = getPriceData(product)

                    if (priceData && product) {
                        product.priceData = priceData
                    }

                    if (product?.representedProduct) {
                        product.representedProduct.priceData = priceData
                        product.representedProduct.c_imageBadges = product?.c_imageBadges
                        product.representedProduct.c_textBadges = product?.c_textBadges
                    }
                    const selectedVariant = product?.variants?.find(
                        (v) => v?.productId === product?.representedProduct?.id
                    )

                    let productData = this.productData(
                        product.representedProduct || product,
                        selectedVariant || product?.variants?.[0] || null,
                        product.index + 1,
                        false,
                        category,
                        listId
                    )

                    return {
                        ...productData,
                        category: (breadcrumb || '').replaceAll(' / ', '/') || '',
                        list: category
                            ? `plp-${
                                  category?.name ||
                                  category?.c_name ||
                                  (typeof category === 'string' && category?.toLocaleLowerCase()) ||
                                  ''
                              }`
                            : 'search results'
                    }
                })

                this.push({
                    event: 'product-impression',
                    ecommerce: {
                        currencyCode: getActiveCurrency(),
                        impressions: impressions
                    }
                })
            })
        },
        viewItemLists(hits, sorting, options = {}, isColorBar) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const {method, listId, listName} = options
            this.runAfterDataLayerLoad(() => {
                if (!hits?.length) return

                const itemsAdded = hits
                    ?.filter((item) => {
                        if (!item) {
                            console.warn('Skipping invalid item:', item)
                            return false
                        }
                        return true
                    })
                    .map((product, index) => {
                        const priceData = getPriceData(product)

                        return {
                            product: {
                                ...product,
                                priceData
                            },
                            index: product.index ?? index
                        }
                    })

                // No valid items for view_item_list, skipping dataLayer push
                if (!itemsAdded?.length) {
                    return
                }
                const firstProduct = hits[0]
                const contextMethod = method || 'plp'
                const contextListId = listId || 'plp'
                const contextListName =
                    listName ||
                    `plp${
                        firstProduct?.c_primaryCategory
                            ? ` - ${firstProduct.c_primaryCategory}`
                            : ''
                    }`
                this.trackGA4Event({
                    eventName: 'view_item_list',
                    itemsAdded: itemsAdded,
                    variant: {pid: firstProduct?.id},
                    sorting: sorting,
                    method: contextMethod,
                    currency: getActiveCurrency(),
                    listId: contextListId,
                    listName: contextListName,
                    isColorBar: isColorBar
                })
            })
        },
        productClick(
            listName,
            product,
            selectedVariant,
            index,
            breadcrumb,
            category,
            listId,
            sorting
        ) {
            if (!googleTagManagerEnabled) {
                return false
            }
            const priceData = getPriceData(product)
            if (priceData && product) {
                product.priceData = priceData
            }
            if (product?.representedProduct) {
                product.representedProduct.priceData = priceData
                product.representedProduct.c_imageBadges = product?.c_imageBadges
            }
            var productData = this.productData(
                product?.representedProduct || product,
                selectedVariant,
                index,
                false,
                category,
                listId
            )

            productData = {
                ...productData,
                category: breadcrumb?.replaceAll(' / ', '/') || '',
                list: listName
            }
            this.push({
                event: 'product-click',
                ecommerce: {
                    click: {
                        actionField: {list: listName},
                        products: [productData]
                    }
                }
            })
            const {itemId} = getSelectItemVariantData(
                {product, variant: selectedVariant},
                {pid: productData?.id}
            )
            this.trackGA4Event({
                eventName: 'select_item',
                itemsAdded: [{product}],
                variant: {pid: itemId},
                method: 'pdp',
                sorting: sorting,
                currency: getActiveCurrency(),
                listId: listId,
                listName: listName,
                indexPosition: index
            })
        },
        productView(
            product,
            selectedVariant,
            isEngravable,
            isColorBar,
            isGiftCard,
            componentProps = {}
        ) {
            if (!googleTagManagerEnabled || !product) {
                return false
            }
            this.runAfterDataLayerLoad(() => {
                const priceData = getPriceData(product)
                if (priceData && product) {
                    product.priceData = priceData
                }
                if (product?.representedProduct) {
                    product.representedProduct.priceData = priceData
                    product.representedProduct.c_imageBadges = product?.c_imageBadges
                }
                var productData = this.productData(
                    product.representedProduct || product,
                    selectedVariant,
                    null,
                    null,
                    null,
                    null,
                    isEngravable,
                    isColorBar,
                    isGiftCard
                )
                // Attach any component-level analytics props provided from the originating list/reco
                if (
                    Object.keys(componentProps).length > 0 &&
                    productData.id === componentProps.id
                ) {
                    try {
                        const {...analyticsProps} = componentProps
                        productData.component_attribution = analyticsProps
                    } catch (e) {
                        console.warn(e.message)
                    }
                }
                const eventData = {
                    event: 'productDetail',
                    event_id: window.fbEventID,
                    ecommerce: {
                        detail: {
                            products: [productData]
                        }
                    }
                }
                if (isGiftCard) {
                    eventData.list = 'egift card'
                }
                this.push(eventData)

                this.trackGA4Event({
                    eventName: 'view_item',
                    itemsAdded: [{product: product}],
                    variant: {pid: productData?.id},
                    method: window.listId || 'pdp',
                    currency: getActiveCurrency(),
                    listId: window.listId || 'pdp',
                    listName: window.listId || 'pdp'
                })
            })
        },
        getCartItemsData(
            pwaProducts,
            cartProducts,
            indexPosition,
            isColorBar,
            eventName,
            listName,
            listID,
            oosSKUs
        ) {
            let productItems = []
            let priceTotal = 0
            if (listID === 'cart' || eventName === 'purchase') {
                pwaProducts.forEach((item, index) => {
                    let product = {}
                    const vv = item?.variationValues || {}
                    const colorValueID =
                        vv.stoneColor || vv.metal || vv.colorGeneral || item?.c_colorGeneral
                    const sizeValueID =
                        vv.size || vv.letter || item?.c_size || item?.c_RingSize?.[0]

                    let colorValue = ''
                    item?.variationAttributes?.forEach((attribute) => {
                        if (
                            attribute?.id == 'stoneColor' ||
                            attribute?.id === 'metal' ||
                            attribute?.id === 'colorGeneral'
                        ) {
                            colorValue = attribute?.values?.filter(
                                (value) => value.value === colorValueID
                            )?.[0]?.name
                            return
                        }
                    })
                    let sizeValue = ''
                    item?.variationAttributes?.forEach((attribute) => {
                        if (attribute?.id == 'size' || attribute?.id == 'letter') {
                            sizeValue = attribute?.values?.filter(
                                (value) => value.value === sizeValueID
                            )?.[0]?.name
                            return
                        }
                    })
                    if (!sizeValue) {
                        sizeValue =
                            item?.c_size || item?.c_RingSize?.[0] || cartProducts?.[index]?.c_size
                    }

                    const itemVariantArray = item?.c_isEngravable
                        ? [colorValue, sizeValue, 'Engravable']
                        : [colorValue, sizeValue]
                    const itemColorbarArray = [item?.c_color, item?.c_size]
                    const variantArray = isColorBar ? itemColorbarArray : itemVariantArray
                    const item_size = isColorBar ? product?.c_size : sizeValue

                    const item_color = isColorBar
                        ? product?.c_color
                        : colorValue || product?.configuration?.base_sku

                    product.item_id = isColorBar ? 'colorbar' : item.id
                    product.item_name = normalizeItemName(item.name)
                    product.item_brand = item.brand || 'Kendra Scott'
                    product.item_variant = variantArray?.filter(Boolean).join(' - ') || undefined
                    product.price =
                        cartProducts?.[index]?.amount ||
                        parseFloat(
                            (
                                cartProducts?.[index]?.priceAfterItemDiscount /
                                (cartProducts?.[index]?.quantity ||
                                    cartProducts?.[index]?.quantity ||
                                    1)
                            ).toFixed(2)
                        )
                    product.quantity =
                        cartProducts?.[index]?.stepQuantity || cartProducts?.[index]?.quantity || 1
                    const analytics = parseAnalyticsAttributes(
                        cartProducts?.[index]?.c_analyticsAttributes
                    )
                    product.item_list_id = window.listId || analytics?.listID || listID || undefined
                    product.item_list_name =
                        window.listId || analytics?.listName || listName || undefined
                    product.item_sort = analytics?.itemSort || undefined
                    // Report the badges this line actually renders (see item-variant/item-badges),
                    // falling back to the set stamped on the line at add-to-cart time.
                    product.item_badge =
                        getLineItemBadge({...item, ...cartProducts?.[index]}) ??
                        analytics?.itemBadge ??
                        null
                    product.discount = getDiscount(
                        getPriceData(item),
                        cartProducts?.[index]?.stepQuantity
                    )
                    product.item_size = item_size || undefined
                    product.item_color = item_color || undefined
                    product.is_new = item?.c_isNewtest != null ? String(item.c_isNewtest) : 'false'
                    product.is_bestseller =
                        item?.c_isBestSeller != null ? String(item.c_isBestSeller) : 'false'
                    product.item_category =
                        item?.c_primaryCategory ||
                        item?.primaryCategoryId ||
                        cartProducts?.[index]?.colorbarCategory ||
                        undefined
                    product.index =
                        indexPosition || item.index || eventName === 'purchase'
                            ? index + 1
                            : undefined
                    product.category_id =
                        item?.c_primaryCategory ||
                        item?.primaryCategoryId ||
                        cartProducts?.[index]?.colorbarCategory ||
                        undefined
                    product.parent_id =
                        item?.master?.masterId ||
                        item?.c_masterVariantID ||
                        item?.id ||
                        cartProducts?.[index]?.pid ||
                        undefined
                    priceTotal = priceTotal + product.price * product.quantity
                    productItems.push(product)
                })
                return {
                    productItems: productItems,
                    priceTotal: priceTotal
                }
            } else {
                cartProducts.forEach((cartItem, index) => {
                    isColorBar = cartItem?.productId === 'colorbar'
                    const item = pwaProducts?.[cartItem?.productId || cartItem?.c_productID]
                    let product = {}
                    const vv = item?.variationValues || {}
                    const colorValueID =
                        vv.stoneColor || vv.metal || vv.colorGeneral || item?.c_colorGeneral
                    const sizeValueID = vv.size || item?.c_size || item?.c_RingSize?.[0]

                    let colorValue = ''
                    item?.variationAttributes?.forEach((attribute) => {
                        if (
                            attribute?.id == 'stoneColor' ||
                            attribute?.id === 'metal' ||
                            attribute?.id === 'colorGeneral'
                        ) {
                            colorValue = attribute?.values?.filter(
                                (value) => value.value === colorValueID
                            )?.[0]?.name
                            return
                        }
                    })
                    let sizeValue = ''
                    item?.variationAttributes?.forEach((attribute) => {
                        if (attribute?.id == 'size' || attribute?.id == 'letter') {
                            sizeValue = attribute?.values?.filter(
                                (value) => value.value === sizeValueID
                            )?.[0]?.name
                            return
                        }
                    })
                    const itemVariantArray = item?.c_isEngravable
                        ? [colorValue, sizeValue, 'Engravable']
                        : [colorValue, sizeValue]
                    const itemColorbarArray = [item?.c_color, item?.c_size]
                    const variantArray = isColorBar ? itemColorbarArray : itemVariantArray
                    const item_size = isColorBar ? product?.c_size : sizeValue

                    const item_color = isColorBar
                        ? product?.c_color
                        : colorValue || product?.configuration?.base_sku

                    const analytics = parseAnalyticsAttributes(cartItem?.c_analyticsAttributes)
                    product.item_id = isColorBar ? 'colorbar' : item?.id
                    product.item_name = normalizeItemName(item?.name)
                    product.item_brand = item?.brand || 'Kendra Scott'
                    product.item_variant = variantArray?.filter(Boolean).join(' - ') || undefined
                    product.quantity = cartItem?.quantity
                    const mergedDetails = {...pwaProducts?.[cartItem?.productId], ...cartItem}
                    const itemPriceData = getPriceData(mergedDetails, cartItem?.quantity)
                    product.price =
                        cartItem?.amount ||
                        parseFloat(
                            (cartItem?.priceAfterItemDiscount / cartItem?.quantity).toFixed(2)
                        )
                    product.item_list_id = window.listId || analytics?.listID || listID || undefined
                    product.item_list_name =
                        window.listId || analytics?.listName || listName || undefined
                    product.item_sort = analytics?.itemSort || undefined
                    // Report the badges this line actually renders (see item-variant/item-badges),
                    // falling back to the set stamped on the line at add-to-cart time.
                    product.item_badge =
                        getLineItemBadge(mergedDetails) ?? analytics?.itemBadge ?? null
                    product.discount = getDiscount(itemPriceData, cartItem?.quantity)
                    product.item_size = item_size || undefined
                    product.item_color = item_color || undefined
                    product.is_new = item?.c_isNewtest != null ? String(item.c_isNewtest) : 'false'
                    product.is_bestseller =
                        item?.c_isBestSeller != null ? String(item.c_isBestSeller) : 'false'
                    product.item_category =
                        item?.c_primaryCategory ||
                        item?.primaryCategoryId ||
                        cartItem?.colorbarCategory ||
                        undefined
                    product.index = index + 1
                    product.category_id =
                        item?.c_primaryCategory ||
                        item?.primaryCategoryId ||
                        cartItem?.colorbarCategory ||
                        undefined
                    product.parent_id =
                        item?.master?.masterId ||
                        item?.c_masterVariantID ||
                        item?.id ||
                        cartItem?.pid ||
                        undefined
                    priceTotal = priceTotal + (cartItem?.amount || itemPriceData?.currentPrice || 0) // Fix the price here

                    let isInStock = true

                    // check oos skus array for BOPIS/GlobalE MAO requests
                    if (oosSKUs?.length > 0) {
                        isInStock = !(
                            (
                                oosSKUs.indexOf(cartItem?.c_sku) > -1 ||
                                oosSKUs.indexOf(cartItem?.productId) > -1 ||
                                cartItem?.productId === 'colorbar'
                            ) // colorbar is by default oos for globale/BOPIS
                        )
                    } else {
                        isInStock = item?.inventory?.ats > 0
                    }
                    product.is_in_stock = isInStock ? 'true' : 'false'
                    productItems.push(product)
                })
                return {
                    productItems: productItems,
                    priceTotal: priceTotal
                }
            }
        },
        getItemsData(
            itemsAdded,
            variant,
            sorting,
            indexPosition,
            isColorBar,
            eventName,
            listName,
            listID
        ) {
            let productItems = []
            let priceTotal = 0
            itemsAdded.forEach((item) => {
                let product = {}
                const selectedVariant = item?.product?.variants?.find(
                    (candidate) => candidate.productId === variant?.pid
                )
                const itemId =
                    eventName === 'select_item' && variant?.pid ? variant.pid : item.product.id
                const vv =
                    item.variant?.variationValues ||
                    item.product?.variationValues ||
                    selectedVariant?.variationValues ||
                    {}
                const colorValueID =
                    vv.stoneColor || vv.metal || vv.colorGeneral || item.product?.c_colorGeneral
                const sizeValueID = vv.size || item.product?.c_size || item.product?.c_RingSize?.[0]
                let colorValue = ''
                item?.product?.variationAttributes?.forEach((attribute) => {
                    if (
                        attribute?.id == 'stoneColor' ||
                        attribute?.id === 'metal' ||
                        attribute?.id === 'colorGeneral'
                    ) {
                        colorValue = attribute?.values?.filter(
                            (value) => value.value === colorValueID
                        )?.[0]?.name
                        return
                    }
                })
                let sizeValue = ''
                item?.product?.variationAttributes?.forEach((attribute) => {
                    if (attribute?.id == 'size' || attribute?.id == 'letter') {
                        sizeValue = attribute?.values?.filter(
                            (value) => value.value === sizeValueID
                        )?.[0]?.name
                        return
                    }
                })

                if (!colorValue) {
                    colorValue =
                        item.product?.c_metal ||
                        item.product?.c_color ||
                        item.product?.c_colorGeneral
                }

                if (!sizeValue) {
                    sizeValue = item.product?.c_size || item.product?.c_RingSize?.[0]
                }

                const inventory = item.product?.inventory
                const stockLevel = Number(inventory?.stockLevel || 0)
                const itemVariantArray = item?.product?.c_isEngravable
                    ? [colorValue, sizeValue, 'Engravable']
                    : [colorValue, sizeValue]
                const itemColorbarArray = [item?.product?.c_color, item?.product?.c_size]
                const variantArray = isColorBar ? itemColorbarArray : itemVariantArray
                const item_size = isColorBar ? product?.c_size : sizeValue

                const item_color = isColorBar
                    ? product?.c_color
                    : colorValue || product?.configuration?.base_sku
                const itemPriceData = item.product?.priceData || getPriceData(item.product)
                const productQuantity =
                    item.custom?.quantity ||
                    item.product?.quantity ||
                    item.product?.stepQuantity ||
                    1
                const cartUnitPrice = getCartLineUnitPrice(item.product)

                product.item_id = isColorBar ? 'colorbar' : itemId
                product.item_name = normalizeItemName(item.product.name)
                product.item_brand = item.product.brand || 'Kendra Scott'
                product.item_variant = variantArray?.filter(Boolean).join(' - ') || undefined
                const productPrice =
                    item.custom?.price ??
                    cartUnitPrice ??
                    window.updatedItemPrice ??
                    (itemPriceData?.currentPrice !== undefined
                        ? itemPriceData?.currentPrice
                        : item.product.price)
                product.price = productPrice
                product.quantity = productQuantity
                product.item_list_id = window.listId || listID || undefined
                product.item_list_name = window.listId || listName || undefined
                product.item_sort = sorting || undefined
                product.item_size = item_size || undefined
                product.item_color = item_color || undefined
                product.is_new =
                    item.product?.c_isNewtest != null ? String(item.product.c_isNewtest) : 'false'
                product.is_bestseller =
                    item.product?.c_isBestSeller != null
                        ? String(item.product.c_isBestSeller)
                        : 'false'
                product.item_category =
                    item.product?.c_primaryCategory ||
                    item.product?.primaryCategoryId ||
                    variant?.colorbarCategory ||
                    undefined
                product.index =
                    listID === 'pdp' ? undefined : indexPosition || item.product.index || undefined
                product.category_id =
                    item.product?.c_primaryCategory ||
                    item.product?.primaryCategoryId ||
                    variant?.colorbarCategory ||
                    undefined
                product.parent_id =
                    item.product?.master?.masterId ||
                    item.product?.c_masterVariantID ||
                    item.product?.id ||
                    variant?.pid ||
                    undefined
                if (
                    eventName === 'view_item' ||
                    eventName === 'view_item_list' ||
                    eventName === 'add_to_cart' ||
                    listID === 'cart' ||
                    eventName == 'add_to_wishlist'
                ) {
                    product.is_in_stock = String(inventory?.orderable === true && stockLevel > 0)
                }
                product.discount = getDiscount(itemPriceData, productQuantity)
                // Cart-surface events report the badges the cart line renders (price adjustments,
                // Final Sale, shipping promos); elsewhere it's the tile's promo callouts.
                product.item_badge =
                    listID === 'cart'
                        ? getLineItemBadge({...item.product, ...item})
                        : getItemBadge(item.product, {selectedVariant})
                // `value` is the monetary amount of the event, so scale the unit price by the
                // units involved. Callers that pass no quantity keep the previous behaviour.
                priceTotal = priceTotal + productPrice * productQuantity
                productItems.push(product)
            })
            return {
                productItems: productItems,
                priceTotal: priceTotal
            }
        },
        addToCart(
            products,
            selectedVariant,
            productItems,
            category,
            productSource,
            meterType,
            isPreOrder = false,
            engravingGtmObject,
            isColorbar,
            isGiftCard,
            isSet = false,
            componentProps = {}
        ) {
            if (isColorbar) {
                // for colorbar products = recipe and selectedVariant = fluid configurations
                const stoneType = getColorBarStonesNames(products)
                this.push({
                    event: 'addToCart',
                    itemColor: products?.configuration?.base_sku,
                    stoneType: stoneType,
                    ecommerce: {
                        giftAssistant: 'false',
                        giftcardProduct: false,
                        add: {
                            products: [
                                {
                                    id: selectedVariant?.finishedsku || selectedVariant?.pid,
                                    name: selectedVariant?.productName || 'Color Bar',
                                    category: selectedVariant?.colorbarCategory,
                                    price: selectedVariant?.fluidConfigurePrice,
                                    brand: 'Kendra Scott',
                                    parent_id: selectedVariant?.pid,
                                    category_id: selectedVariant?.colorbarCategory,
                                    quantity: Number(selectedVariant?.fluidConfigureQuantity) || 1,
                                    dimension92: 'no badge'
                                }
                            ]
                        }
                    }
                })

                return
            }
            if (!products?.length) {
                const priceData = getPriceData(products)
                products.priceData = priceData
            }
            if (products?.representedProduct) {
                if (products.priceData) {
                    products.representedProduct.priceData = products.priceData
                }
                products.representedProduct.c_imageBadges = products?.c_imageBadges
            }
            var productData = products?.length
                ? productItems
                      .map((item, index) => {
                          let product = products.find((p) =>
                              p.variants?.some((v) => v.productId === item.productId && v.orderable)
                          )
                          const variant = product?.variants?.find(
                              (v) => v.productId === item.productId
                          )
                          if ((!product || !variant) && !isSet) return null
                          // Always work on a cloned product to avoid mutating `products`
                          const baseProduct = product || products[index]
                          const clonedProduct = {...baseProduct}
                          if (isSet && item?.variant) {
                              clonedProduct.id = item.variant.productId
                              clonedProduct.name = item.variant.name
                              clonedProduct.price = item.variant.price
                          }
                          return this.productData(
                              clonedProduct?.representedProduct || clonedProduct,
                              variant,
                              null,
                              true,
                              category,
                              null,
                              null,
                              isColorbar,
                              isGiftCard,
                              componentProps
                          )
                      })
                      .filter(Boolean)
                : [
                      this.productData(
                          products.representedProduct || products,
                          selectedVariant,
                          null,
                          true,
                          category,
                          null,
                          null,
                          isColorbar,
                          isGiftCard,
                          componentProps
                      )
                  ]
            const promoFields = meterType
                ? {
                      add_method: meterType,
                      promo: meterType
                          ? {
                                is_bonus: 'true'
                            }
                          : null
                  }
                : {}
            const preOrderFields = isPreOrder
                ? {add_method: 'pre-order', eventType: 'cta', eventAction: 'add to cart'}
                : {}
            const parentProperties = engravingGtmObject?.mainEngravingData
                ? {...engravingGtmObject?.mainEngravingData}
                : {addEngraving: 'no'}
            if (engravingGtmObject?.productEngravingData && productData?.length) {
                productData[0] = {
                    ...productData[0],
                    ...engravingGtmObject?.productEngravingData
                }
            }

            this.push({
                event: 'addToCart',
                ...preOrderFields,
                ecommerce: {
                    giftAssistant: 'false',
                    giftcardProduct: false,
                    ...parentProperties,
                    add: {
                        products: productData
                    }
                },
                productSource: productSource,
                ...promoFields
            })

            if (window.fbEventID) {
                const eventID = window.fbEventID

                window.fbEventIdAddToCart = eventID

                this.push({
                    event: 'addToCartFbEvent',
                    addToCartEventId: eventID
                })
            }
        },
        addToCartGA4({
            itemsAdded,
            variant,
            method,
            currency,
            sorting = undefined,
            indexPosition = undefined,
            isColorBar = false,
            listName = undefined,
            listId = undefined
        }) {
            const {productItems, priceTotal} = this.getItemsData(
                itemsAdded,
                variant,
                sorting,
                indexPosition,
                isColorBar,
                'add_to_cart',
                listName,
                listId
            )

            this.push({ecommerce: null})
            this.push({
                event: 'add_to_cart',
                add_method: method, //pdp|upsell|quick add|cart|plp|cross-sell|color bar|pre-order
                ecommerce: {
                    currency: getActiveCurrency(currency),
                    value: priceTotal, //The monetary value of the event
                    items: productItems
                }
            })
        },
        bonusSoldOut(product, category, meterType) {
            if (!googleTagManagerEnabled) {
                return false
            }
            product = this.productData(product, product, null, true, category)
            this.push({
                event: 'bonus-sold-out',
                add_method: meterType,
                ecommerce: {
                    products: [product]
                },
                promo: {
                    is_bonus: 'true'
                }
            })
        },
        popUpshown(thirdPartyName, popUpCount, popUpCountTota) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'popUp-shown',
                'third-party-name': thirdPartyName,
                'popUp-count': popUpCount,
                'popUp-count-total': popUpCountTota
            })
        },
        wishlistInteraction(category, productId, productName, totalWishlistItems, wishlistAction) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'wishlistInteraction',
                productCategory: category,
                productID: productId,
                productName: productName,
                totalWishlistItems: totalWishlistItems,
                wishlistAction: wishlistAction,
                wishlistLocation: window.location.href
            })
        },
        trackGA4Event({
            eventName,
            itemsAdded,
            variant = {},
            method,
            currency,
            sorting,
            indexPosition = undefined,
            isColorBar = false,
            listId = '',
            listName = ''
        }) {
            if (!googleTagManagerEnabled) {
                return false
            }
            let {productItems, priceTotal} = this.getItemsData(
                itemsAdded,
                variant,
                sorting,
                indexPosition,
                isColorBar,
                eventName,
                listName,
                listId
            )

            productItems = productItems.map((item, index) => ({
                ...item,
                item_list_id: window.listId || listId || '',
                item_list_name: window.listId || listName.toLowerCase() || '',
                index: listId === 'pdp' ? undefined : item.index ?? index + 1,
                quantity: item.quantity ?? 1,
                price: item.price ?? 0
            }))
            const isCartSurfaceEvent = method === 'cart' || listId === 'cart'
            const currentPrice = itemsAdded?.[0]?.product?.priceData?.currentPrice
            const finalValue = isCartSurfaceEvent
                ? productItems.reduce(
                      (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
                      0
                  )
                : currentPrice !== undefined
                ? currentPrice
                : priceTotal ??
                  productItems.reduce(
                      (sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1),
                      0
                  )

            this.push({ecommerce: null})

            const excludedAddMethodEvents = [
                'view_item_list',
                'remove_from_wishlist',
                'select_item'
            ]
            const excludedValueEvents = ['view_item_list']

            const shouldIncludeAddMethod = !excludedAddMethodEvents.includes(eventName) && method

            const shouldIncludeValue =
                !excludedValueEvents.includes(eventName) && finalValue !== undefined
            const payload = {
                event: eventName,
                ...(shouldIncludeAddMethod && {
                    add_method: method
                }),
                ecommerce: {
                    currency: getActiveCurrency(currency),
                    ...(shouldIncludeValue && {
                        value: finalValue
                    }),
                    items: productItems
                }
            }

            this.push(payload)
        },

        bopisPDPClick() {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'bopisPDPClick'
            })
        },
        bopisFindMyStore(term) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'find-my-store-search',
                locationSearchTerm: term
            })
        },
        storeDetailsClick(storeName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'store-details-click',
                storeName: storeName
            })
        },
        setAsMyStore(storeName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'set-as-my-store',
                storeName: storeName
            })
        },
        directoryClick() {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'directory-click'
            })
        },
        useMyLocation() {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'use-my-location-search'
            })
        },
        reviewsClick(productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'reviews-click',
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - reviews link',
                eventLabel: productName
            })
        },
        pdpCtaClick(eventAction, eventLabel) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'pdp-cta-click',
                eventCategory: 'PDP Interaction',
                eventAction: eventAction,
                eventLabel: eventLabel
            })
        },
        virtualTryOnClick(productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'virtualTryOnClick',
                virtualTryOnLocation: 'pdp',
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - Open',
                eventLabel: productName
            })
        },
        virtualTryOnConsent(choice, productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'virtualTryOnConsent',
                vtoconsentClick: choice,
                eventCategory: 'PDP Interaction',
                eventAction: `cta click - ${choice}`,
                eventLabel: productName
            })
        },
        mirelz360Open(productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'mirelz360Open',
                mirelz360Location: 'pdp',
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - Open',
                eventLabel: productName
            })
        },
        mirelz360Close(productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'mirelz360Close',
                mirelz360Location: 'pdp',
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - Close',
                eventLabel: productName
            })
        },
        pdpColorSelected(itemColor, colorType) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'color-select', itemColor: itemColor, colorType: colorType})
        },
        virtualTryOnClose(productName) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'virtualTryOnClose',
                virtualTryOnLocation: 'pdp',
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - Close',
                eventLabel: productName
            })
        },
        purchasePathMessage({productID, productName, productCategory, messageType, messageText}) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.runAfterDataLayerLoad(() => {
                this.push({
                    event: 'purchasePathMessage',
                    productID: productID,
                    productName: productName,
                    productCategory: productCategory,
                    messageType: messageType,
                    messageText: messageText
                })
            })
        },
        productScroll(scrollPercent, productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'productScroll',
                scrollPercent: scrollPercent,
                productName: productName
            })
        },
        thumbnailClick(index, productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'thumbnail-click',
                imageNumber: index,
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - product image',
                eventLabel: productName
            })
        },
        videoClick(index, productName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'thumbnail-click',
                imageNumber: index,
                eventCategory: 'PDP Interaction',
                eventAction: 'cta click - product video',
                eventLabel: productName
            })
        },
        globalDataLoad: async (
            pageType,
            pageCategory,
            totalQuantity,
            productTotal,
            customer,
            city,
            country,
            state,
            searchQuery,
            totalItems
        ) => {
            if (!googleTagManagerEnabled) {
                return false
            }
            const pageTitle = document.querySelector('title').innerHTML
            const hashEmail = async (email) => {
                const encoder = new TextEncoder()
                const data = encoder.encode(email)
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
                const hashArray = Array.from(new Uint8Array(hashBuffer))
                const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
                return hashHex
            }
            const hashedEmail = customer?.email ? await hashEmail(customer?.email) : ''
            const userSearchQuery = searchQuery ? {searchTerm: searchQuery} : {}
            const searchResults =
                totalItems !== undefined ? {searchResults: totalItems?.toString()} : {}
            window.dataLayer.push({
                pageType: pageType,
                pageCategory: pageCategory,
                pageTitle: pageTitle,
                cartTotalItems: totalQuantity,
                cartTotalValue: productTotal,
                email1: customer?.email || '',
                email256: hashedEmail || '',
                userID: customer?.customerNo || '',
                visitorLoginState:
                    customer?.authType === 'registered'
                        ? customer?.c_clubKendraMembershipActive
                            ? 'ck_member'
                            : 'registered'
                        : 'guest',
                city: city === 'undefined' ? undefined : city,
                country: country,
                state: state === 'undefined' ? undefined : state,
                loyalty_state: customer?.authType === 'registered' ? true : false,
                ...userSearchQuery,
                ...searchResults
            })
        },
        loyaltyRegistrationError(validationMsg) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'loyalty-registration-error',
                failReason: validationMsg
            })
        },
        errorDisplayed(errorType, errorMsg, errorContext) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'error_displayed',
                error_type: errorType,
                error_message: errorMsg,
                error_context: errorContext
            })
        },
        optiversalClick(clickText, clickURL) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'optiversal_click',
                click_text: clickText,
                link_url: clickURL,
                link_domain: 'kendrascott.com'
            })
        },
        promoMessageImpression(promo) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.runAfterDataLayerLoad(() => {
                this.push({
                    event: 'promotion-impression',
                    ecommerce: {
                        promoView: {
                            promotions: [promo]
                        }
                    }
                })
                this.push({
                    event: 'view_promotion',
                    creative_name: promo.creative || promo.name,
                    creative_slot: promo.position,
                    promotion_id: promo.id,
                    promotion_name: promo.name
                })
            })
        },
        promoMeterImpression(promo, method, threshold) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.runAfterDataLayerLoad(() => {
                this.push({
                    event: 'promotion-impression',
                    add_method: method,
                    ecommerce: {
                        promoView: {
                            promotions: [promo]
                        }
                    },
                    promo: {
                        threshold: threshold // Possible Values: Mid, Empty, Full
                    }
                })

                this.push({
                    event: 'view_promotion',
                    creative_name: promo.creative || promo.name,
                    creative_slot: promo.position,
                    promotion_id: promo.id,
                    promotion_name: promo.name
                })
            })
        },
        iAdvizeAddToBag(product, productSource) {
            const {productName, productId, price} = product
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'addToCart',
                add_method: 'iadvize-chat',
                ecommerce: {
                    giftAssistant: 'false',
                    giftcardProduct: false,
                    add: {
                        products: [
                            {
                                name: productName,
                                id: productId,
                                price: price,
                                quantity: 1 // hardcode
                            }
                        ]
                    }
                },
                productSource: productSource
            })
        },
        promotionClick(promo, meterType) {
            if (!googleTagManagerEnabled) {
                return false
            }
            const {id, name, creative, position, quantity} = promo
            const promoFields = meterType
                ? {
                      add_method: meterType,
                      promo: meterType
                          ? {
                                is_bonus: 'true'
                            }
                          : null
                  }
                : {}
            this.push({
                event: 'promotion-click',
                ecommerce: {
                    promoClick: {
                        promotions: [
                            {
                                id: id,
                                name: name,
                                creative: creative,
                                position: position,
                                quantity: quantity || undefined
                            }
                        ]
                    }
                },
                ...promoFields
            })

            this.push({
                event: 'select_promotion',
                creative_name: name,
                creative_slot: creative,
                promotion_id: id,
                promotion_name: name
            })
        },
        openPdBasket(location) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'view_cart',
                event_type: 'cta',
                event_action: 'view pre-order cart',
                event_location: location,
                add_method: 'pre-order'
            })
        },
        openEngravingModal() {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'add-engraving-click',
                addEngraving: 'yes'
            })
        },
        engravingText(engravedText) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'engraving-text', engravingText: engravedText})
        },
        engravingSymbol(engravedSymbol) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'engraving-symbol',
                engravingSymbol: `{{${engravedSymbol}}}`
            })
        },
        engravingZodiacSign(engravedZodiacSign) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'engraving-Zodiacsign',
                engravingZodiacsign: `{{${engravedZodiacSign}}}`
            })
        },
        doNotEngrave() {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'add-engraving-click', addEngraving: 'no'})
        },
        saveEngraving() {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'add-engraving-click', addEngraving: 'yes'})
        },
        headerInteraction(ctaText) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'header_interaction', cta_text: ctaText})
        },
        newsletterFbSubscribe() {
            if (!googleTagManagerEnabled) {
                return false
            }

            if (window.fbEventID) {
                const eventID = window.fbEventID

                window.fbEventIdLead = eventID

                this.push({
                    event: 'leadFbEvent',
                    leadEventId: eventID
                })
            }
        },
        colorBarMetalStoneSelect(itemColor) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'color-select',
                itemColor: itemColor
            })
        },
        ctaInteraction(ctaText) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({event: 'cta_click', cta_text: ctaText})
        },
        loginInitiated() {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'login_initiated'
            })
        },
        loginSuccess(customer) {
            if (!googleTagManagerEnabled || !customer) {
                return false
            }

            const getVisitorState = () => {
                if (customer?.authType === 'registered') {
                    return customer?.c_clubKendraMembershipActive ? 'ck_member' : 'registered'
                }
                return 'guest'
            }

            const hashEmail = async (email) => {
                if (!email) return ''
                const encoder = new TextEncoder()
                const data = encoder.encode(email.toLowerCase().trim())
                const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
                const hashArray = Array.from(new Uint8Array(hashBuffer))
                return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
            }

            ;(async () => {
                const hashedEmail = await hashEmail(customer?.email)

                this.push({
                    event: 'login_success',
                    user_id: customer?.customerNo || '',
                    email256: hashedEmail || '',
                    visitor_login_state: getVisitorState()
                })
            })()
        },
        /**
         * Reports the units that left the cart: `quantity` is the amount removed (1 when a
         * 5 goes to 4, 5 when the line is removed outright), `price` is the unit price and
         * `value` is price * quantity — the amount subtracted from the cart total.
         *
         * @param cartProduct - the line item as it was before the removal
         * @param pwaProduct - the matching product from the product cache
         * @param removedQuantity - units removed, not the quantity left behind
         * @param coupon - comma separated coupon codes applied to the basket
         */
        removeFromCart(cartProduct, pwaProduct, removedQuantity, coupon) {
            if (!googleTagManagerEnabled) {
                return false
            }
            const {productItems, priceTotal} = this.getCartItemsData(
                [pwaProduct],
                [cartProduct],
                window.updatedIndex,
                pwaProduct?.id === 'colorbar',
                'remove_from_cart',
                'cart',
                'cart'
            )
            const removedQty = removedQuantity || 1
            const qtyBeforeRemoval = cartProduct?.quantity || removedQty
            // Save-to-wishlist sets window.updatedItemPrice from the cart row's displayed total.
            const unitPrice =
                window.updatedItemPrice != null
                    ? Number(window.updatedItemPrice)
                    : qtyBeforeRemoval > 0
                    ? priceTotal / qtyBeforeRemoval
                    : priceTotal
            delete window.updatedItemPrice
            productItems[0].is_in_stock = String(
                pwaProduct?.inventory?.orderable === true && pwaProduct?.inventory?.stockLevel > 0
            )
            productItems[0].quantity = removedQty
            productItems[0].price = Number(unitPrice.toFixed(2))

            this.push({ecommerce: null})
            this.push({
                event: 'remove_from_cart',
                ecommerce: {
                    currency: getActiveCurrency(),
                    value: cartProduct?.c_giftCard
                        ? cartProduct?.amount
                        : Number((unitPrice * removedQty).toFixed(2)) || 0,
                    coupon: coupon || undefined,
                    items: productItems
                }
            })
        },
        addShippingInfo(basket, pwaProducts, coupon) {
            const changeUpProductID = getChangeUpProductID()
            const subTotal = getSubTotal(basket, changeUpProductID)
            if (!googleTagManagerEnabled) {
                return false
            }
            const cartItems = [
                ...(basket?.productItems || []).filter((p) => !p?.c_hiddenFluidProduct),
                ...(basket?.giftCertificateItems || [])
            ]
            const {productItems} = this.getCartItemsData(
                pwaProducts,
                cartItems,
                undefined,
                undefined, // set this only if we are editing one product on cart page
                'add_shipping_info',
                undefined,
                undefined
            )
            this.push({ecommerce: null})
            this.push({
                event: 'add_shipping_info',
                shipping_tier: basket?.shipments?.[0]?.shippingMethod?.name,
                ecommerce: {
                    currency: 'USD', // hardcode
                    value: subTotal,
                    coupon: coupon,
                    items: productItems
                }
            })
        },
        addPaymentInfo(basket, pwaProducts, coupon, paymentType) {
            if (!googleTagManagerEnabled) {
                return false
            }
            const changeUpProductID = getChangeUpProductID()
            const subTotal = getSubTotal(basket, changeUpProductID)
            if (!googleTagManagerEnabled) {
                return false
            }
            const cartItems = [
                ...(basket?.productItems || []).filter((p) => !p?.c_hiddenFluidProduct),
                ...(basket?.giftCertificateItems || [])
            ]
            const {productItems} = this.getCartItemsData(
                pwaProducts,
                cartItems,
                undefined,
                undefined, // set this only if we are editing one product on cart page
                'add_payment_info',
                undefined,
                undefined
            )
            this.push({ecommerce: null})
            this.push({
                event: 'add_payment_info',
                payment_type: paymentType,
                ecommerce: {
                    currency: 'USD', // hardcode
                    value: subTotal,
                    coupon: coupon,
                    items: productItems
                }
            })
        },
        donationRoundup(donationAmount) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'donation_roundup',
                donation_amount: donationAmount
            })
        },
        generalPushEvent(eventName) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: eventName
            })
        },
        pdPurchaseEvent(details) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const items = getPDItemsArray(details?.lineItems)
            this.push({ecommerce: null})
            this.push({
                event: 'purchase',
                checkout_mode: 'pre-order',
                ecommerce: {
                    currency: 'USD', // hardcode,
                    value: details?.total?.amount,
                    payment_type: 'Purple Dot Not Available',
                    tax: details?.tax?.amount,
                    shipping: details?.shipping?.amount,
                    transaction_id: details?.reference,
                    items: items
                }
            })
        },
        /**
         * GA4 purchase event, fired once from the order confirmation page.
         * @param {Object} order - placed order from useOrder
         * @param {Object} pwaProducts - product details keyed by product id
         * @param {Object} customer - current customer from useCurrentCustomer
         * @param {Object} options - order-level context already derived by the page
         */
        purchase(order, pwaProducts, customer, options = {}) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const {
                checkoutMode,
                paymentType,
                shippingCouponCode,
                donationItemId,
                donationAmount = 0
            } = options

            const changeUpProductID = getChangeUpProductID()
            const value = getPurchaseValue(order, changeUpProductID)

            // The donation is a regular line item on the order; it is reported through
            // donation_amount instead of as a purchased product.
            const cartItems = [
                ...(order?.productItems || []).filter(
                    (p) => !p?.c_hiddenFluidProduct && p?.itemId !== donationItemId
                ),
                ...(order?.giftCertificateItems || [])
            ]

            const {productItems} = this.getCartItemsData(
                pwaProducts,
                cartItems,
                undefined,
                undefined,
                'purchase',
                undefined,
                undefined
            )

            const visitorLoginState =
                customer?.authType === 'registered'
                    ? customer?.c_clubKendraMembershipActive
                        ? 'ck_member'
                        : 'registered'
                    : 'guest'

            this.push({ecommerce: null})
            this.push({
                event: 'purchase',
                checkout_mode: checkoutMode,
                attentive_opt_in_status:
                    !!order?.c_AttentivePhoneNo || !!order?.c_AttentiveBopisSMSOptIn,
                coupon_shipping_code: shippingCouponCode || undefined,
                donation_action: donationAmount > 0 ? 'donate' : 'no donation',
                donation_amount: donationAmount,
                loyalty_state: customer?.authType === 'registered',
                visitor_login_state: visitorLoginState,
                ecommerce: {
                    currency: getActiveCurrency(),
                    value: value,
                    // SFRA pipe separates and dedupes the coupons applied across product,
                    // shipping and order level promotions; SCAPI only exposes the codes.
                    coupon:
                        [...new Set((order?.couponItems || []).map((c) => c.code))].join('|') ||
                        undefined,
                    payment_type: paymentType,
                    tax: order?.taxTotal,
                    shipping: order?.shippingTotal,
                    transaction_id: order?.orderNo,
                    items: productItems
                }
            })
        },
        applyPromoCode(promoCode) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: 'apply_coupon',
                promo_code: promoCode
            })
        },
        viewCart(basket, pwaProducts, coupon, oosSKUs) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const changeUpProductID = getChangeUpProductID()
            const subTotal = getSubTotal(basket, changeUpProductID)

            const cartItems = [
                ...(basket?.productItems || []).filter((p) => !p?.c_hiddenFluidProduct),
                ...(basket?.giftCertificateItems || [])
            ]

            const {productItems} = this.getCartItemsData(
                pwaProducts,
                cartItems,
                undefined,
                undefined,
                'view_cart',
                undefined,
                undefined,
                oosSKUs
            )

            this.push({ecommerce: null})

            this.push({
                event: 'view_cart',
                ecommerce: {
                    currency: getActiveCurrency(),
                    value: subTotal,
                    coupon: coupon,
                    items: productItems
                }
            })
        },
        beginCheckout(basket, pwaProducts, customer) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const changeUpProductID = getChangeUpProductID()
            const subTotal = getSubTotal(basket, changeUpProductID)
            const cartItems = [
                ...(basket?.productItems || []).filter((p) => !p?.c_hiddenFluidProduct),
                ...(basket?.giftCertificateItems || [])
            ]
            const {productItems} = this.getCartItemsData(
                pwaProducts,
                cartItems,
                undefined,
                undefined,
                'begin_checkout',
                undefined,
                undefined
            )

            this.push({ecommerce: null})
            this.push({
                event: 'begin_checkout',
                add_engraving: cartItems.some((item) => item?.c_hasEngraving) ? 'yes' : 'no',
                loyalty_state: customer?.authType === 'registered',
                ecommerce: {
                    currency: getActiveCurrency(),
                    value: subTotal,
                    coupon: basket?.couponItems?.map((coupon) => coupon.code).join(',') || '',
                    items: productItems
                }
            })
        },
        pdCheckoutStep(details) {
            if (!googleTagManagerEnabled) {
                return false
            }

            const items = getPDItemsArray(details?.lineItems)

            if (details?.stepNumber === 1) {
                this.push({ecommerce: null})
                this.push({
                    event: 'begin_checkout',
                    checkout_mode: 'pre-order',
                    ecommerce: {
                        currency: 'USD', // hardcode,
                        value: details?.total?.amount,
                        items: items
                    }
                })
            } else if (details?.stepNumber === 2) {
                this.push({
                    event: 'add_shipping_info',
                    checkout_mode: 'pre-order',
                    shipping_tier: 'Purple Dot Not Available',
                    ecommerce: {
                        currency: 'USD', // hardcode,
                        value: details?.total?.amount,
                        items: items
                    }
                })
            } else if (details?.stepNumber === 3) {
                this.push({ecommerce: null})
                this.push({
                    event: 'add_payment_info',
                    checkout_mode: 'pre-order',
                    ecommerce: {
                        currency: 'USD', // hardcode,
                        value: details?.total?.amount,
                        payment_type: 'Purple Dot Not Available',
                        items: items
                    }
                })
            }
        },
        paymentMethodSelection(paymentType) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: 'payment_method_selection',
                payment_type: paymentType
            })
        },
        ctaClick(event, category, action, label, ctaText) {
            if (!googleTagManagerEnabled) {
                return false
            }
            this.push({
                event: event,
                event_category: category,
                event_action: action,
                event_label: label,
                cta_text: ctaText
            })
        },
        checkoutSignup(mailingListOptIn, userId, email256, birthMonth) {
            this.push({
                event: 'signup_success',
                mailing_list_opt_in: mailingListOptIn,
                user_id: userId,
                email256: email256,
                birth_month: birthMonth
            })
        },
        checkoutFormFieldChange(event, category, action, label) {
            if (!googleTagManagerEnabled) {
                return false
            }

            this.push({
                event: event,
                event_category: category,
                event_action: action,
                event_label: label
            })
        }
    }
}
